import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Stack, Button, Card, IconButton, Tooltip, MenuItem, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Popover from '@mui/material/Popover';
import { Edit, Delete, Upload } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { MaterialReactTable } from 'material-react-table';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';

import AddProjectItemModal from './overview/components/popups/AddProjectItemModal';
import { DeleteModal } from './overview/components/popups';
import {
  getProjectLinks,
  createProjectLink,
  updateProjectLink,
  deleteProjectLink,
} from '../../services/projectLinksService';
import { getFileDownloadUrl } from '../../services/fileUploadService';
import { isAdmin } from '../../utils/share';
import Loading from '../../layouts/loading';
import TableWrapper from '../../common/TableWrapper'; // if you have a wrapper like in AssetTable

const getUiType = (item) => {
  const url = item?.linkUrl || '';

  if (/^https?:\/\//i.test(url)) {
    return 'link';
  }

  if (url.startsWith('company_')) {
    return 'file';
  }

  return 'file'; // default
};
const ProjectFiles = () => {
  const { id: projectId } = useParams();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState({ action: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const accountData = useSelector((state) => state?.login?.user || null);
  const isFetchingRef = useRef(false);

  // Fetch files from API
  const fetchFiles = useCallback(async () => {
    try {
      isFetchingRef.current = true;
      setLoading(true);
      const response = await getProjectLinks(projectId);
      const allItems = response?.data?.data || [];
      // Filter only files (type: 'file') for files section
      const fileItems = allItems.filter((item) => item.type === 'file');
      setFiles(fileItems);
    } catch (error) {
      toast.error('Failed to fetch project files');
      console.error(error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchFiles();
    }
  }, [projectId, fetchFiles]);

  // Modal open/close helpers
  const handleAddModalOpen = useCallback(() => {
    setModalOpen({ action: 'add' });
  }, []);
  const handleAddModalClose = useCallback(() => {
    setModalOpen({ action: '' });
    setSelectedItem(null);
  }, []);
  const handleEditModalOpen = useCallback((file) => {
    setSelectedItem(file);
    setModalOpen({ action: 'edit' });
  }, []);
  const handleEditModalClose = useCallback(() => {
    setModalOpen({ action: '' });
    setSelectedItem(null);
  }, []);

  // Add
  const handleAddItem = async (itemData) => {
    try {
      setIsCreating(true);
      const response = await createProjectLink({
        ...itemData,
        projectId,
        createdBy: accountData?.id,
        type: 'file',
      });
      toast.success(response?.data?.message || 'Item added successfully');
      await fetchFiles();
      handleAddModalClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to add item');
    } finally {
      setIsCreating(false);
    }
  };

  // Update
  const handleUpdateItem = async (itemData) => {
    try {
      setIsCreating(true);
      const response = await updateProjectLink(selectedItem.id, {
        ...itemData,
        type: 'file',
      });
      toast.success(response?.data?.message || 'Item updated successfully');
      await fetchFiles();
      setIsCreating(false);
      handleEditModalClose();
    } catch (error) {
      console.error(error);
      setIsCreating(false);
      toast.error(error?.response?.data?.message || 'Failed to update item');
    }
  };

  // Delete
  const handleConfirmDelete = useCallback(async () => {
    if (selectedItem && !isDeleting) {
      try {
        setIsDeleting(true);
        const response = await deleteProjectLink(selectedItem.id);
        toast.success(response?.data?.message || 'Item deleted successfully');

        // Remove locally to avoid refetch loops
        setFiles((prev) => prev.filter((f) => f.id !== selectedItem.id));
        setSelectedItem(null);
        setModalOpen({ action: '' });
      } catch (error) {
        console.error('Delete error:', error);
        toast.error(error?.response?.data?.message || 'Failed to delete item');
      } finally {
        setIsDeleting(false);
      }
    }
  }, [selectedItem, isDeleting]);

  const handleDeleteClick = useCallback((file) => {
    setSelectedItem(file);
    setModalOpen({ action: 'delete' });
  }, []);
  const handleOpenActions = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleCloseActions = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  // Open file (S3 or url)
  const handleFileClick = useCallback(async (file) => {
    try {
      const isS3Key =
        /^(company_|user_|system_|common_)/.test(file.linkUrl) ||
        /^\d+_/.test(file.linkUrl) ||
        !file.linkUrl.includes('://');

      if (isS3Key) {
        const downloadUrl = await getFileDownloadUrl(file.linkUrl);
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const isAbsolute = /^https?:\/\//i.test(file.linkUrl);
      const fileUrl = isAbsolute ? file.linkUrl : `https://${file.linkUrl}`;
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error('Failed to open file');
      console.error('Error opening file:', error);
    }
  }, []);

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'linkTitle',
        header: 'Name',
        size: 150, // acts as min-widt

        enableColumnFilter: true,
        Cell: ({ cell, row }) => {
          const val = cell.getValue();
          return (
            <Tooltip title={val || ''}>
              <Box
                sx={{
                  maxWidth: 200,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  // prevent row selection double-fire when clicking inside action buttons
                  e.stopPropagation();
                  handleFileClick(row.original);
                }}
              >
                {val}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'type',
        header: 'Type',
        enableSorting: false,
        enableColumnFilter: false,
        size: 150,
        Cell: ({ row }) => {
          const url = row.original?.linkUrl || '';

          // Detect types based on URL pattern
          if (/^https?:\/\//i.test(url)) {
            return 'Link';
          }

          if (url.startsWith('company_')) {
            return 'File';
          }

          return 'File'; // fallback default
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableColumnFilter: false,
        size: 120,
        Cell: ({ row }) => {
          const item = row.original;
          const uiType = getUiType(item);

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* FILE TYPE → Download */}
              {uiType === 'file' && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileClick(item); // download/open file
                  }}
                  title="Download File"
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              )}

              {/* LINK TYPE → View */}
              {uiType === 'link' && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileClick(item); // open link
                  }}
                  title="View Link"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              )}

              {/* THREE DOTS → Edit/Delete */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenActions(e, item);
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [handleFileClick, handleEditModalOpen, handleDeleteClick]
  );

  if (loading && !files.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Loading />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Add File Button */}
      {isAdmin() && (
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          <Button variant="contained" startIcon={<Upload />} onClick={handleAddModalOpen} sx={{ minWidth: 150 }}>
            Add File
          </Button>
        </Box>
      )}

      {/* Table */}
      <Card>
        <TableWrapper>
          <MaterialReactTable
            columns={columns}
            data={files}
            enableColumnFilters
            enableColumnActions={false}
            enableGlobalFilter
            enableSorting
            enableDensityToggle
            enableHiding={false}
            enablePagination
            enableFullScreenToggle={false}
            enableRowActions={false}
            getRowId={(row) => row.id}
            muiTablePaperProps={{
              elevation: 0,
              sx:{
                padding:'0px 16px'
              }
            }}
            muiTableContainerProps={{
              sx: {
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
              },
            }}
            muiTableBodyRowProps={{
              sx: { cursor: 'pointer' },
            }}
            muiTopToolbarProps={{
              sx: {
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                
              },
            }}
            initialState={{ showColumnFilters: false, density: 'compact' }}
            renderTopToolbarCustomActions={() => (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start', // left align
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: '#1e1e1eff',
                  }}
                >
                  Project Files
                </Typography>
              </Box>
            )}
            state={{ isLoading: loading }}
          />
        </TableWrapper>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseActions}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            disabled={!isAdmin()}
            onClick={() => {
              handleCloseActions();
              handleEditModalOpen(selectedItem);
            }}
          >
            Edit
          </MenuItem>

          <MenuItem
            disabled={!isAdmin()}
            onClick={() => {
              handleCloseActions();
              handleDeleteClick(selectedItem);
            }}
          >
            Delete
          </MenuItem>
        </Popover>
      </Card>

      {/* Add/Edit Item Modal */}
      <AddProjectItemModal
        open={modalOpen.action === 'add' || modalOpen.action === 'edit'}
        onClose={() => {
          setModalOpen({ action: '' });
          setSelectedItem(null);
        }}
        onSubmit={modalOpen.action === 'add' ? handleAddItem : handleUpdateItem}
        context="files"
        link={selectedItem}
        isLoading={isCreating}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={modalOpen.action === 'delete'}
        onClose={() => setModalOpen({ action: '' })}
        onDelete={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </Stack>
  );
};

export default ProjectFiles;
